import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";

type CampaignItem = {
  id: number;
  title: string;
  slug?: string;
  short_description?: string;
  image_url?: string;
  raised_amount?: number | string;
  goal_amount?: number | string;
};

const siteUrl = "https://www.sevaafoundation.com";
const backendUrl = "https://www.sevaafoundation.com/seva-backend";
const fallbackImage = `${siteUrl}/og-blogs.jpg`;

const getFirstImage = (imageString?: string) => {
  if (!imageString) {
    return "https://via.placeholder.com/600x400?text=No+Image";
  }

  const firstImage = imageString.split(",")[0].trim();

  if (firstImage.startsWith("http://") || firstImage.startsWith("https://")) {
    return firstImage;
  }

  return `${backendUrl}${firstImage}`;
};

const Campaigns = () => {
  const [campaigns, setCampaigns] = useState<CampaignItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/campaigns/list.php`);
        const data = await response.json();

        if (data.success) {
          setCampaigns(data.campaigns.slice(0, 3));
        }
      } catch (error) {
        console.error("Failed to fetch campaigns", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCampaigns();
  }, []);

  const openDonate = (id: number) => {
    window.dispatchEvent(
      new CustomEvent("openDonationModal", { detail: { campaignId: id } })
    );
  };

  const campaignSectionSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "ItemList",
      "@id": `${siteUrl}/#home-campaigns`,
      name: "Current NGO Initiatives by Sevaa Foundation",
      description:
        "Current verified initiatives by Sevaa Foundation for child education, healthcare support, hunger relief, women empowerment and social welfare.",
      url: `${siteUrl}/#campaigns`,
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      numberOfItems: campaigns.length,
      publisher: {
        "@type": "NGO",
        "@id": `${siteUrl}/#ngo`,
        name: "Sevaa Foundation",
        alternateName: "Swadesh Sevaa Charitable Foundation",
        url: siteUrl,
        logo: {
          "@type": "ImageObject",
          url: `${siteUrl}/logo512.png`,
        },
      },
      itemListElement: campaigns.map((campaign, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "DonateAction",
          name: campaign.title,
          description: campaign.short_description || campaign.title,
          image: campaign.image_url ? getFirstImage(campaign.image_url) : fallbackImage,
          url: `${siteUrl}/campaign`,
          recipient: {
            "@type": "NGO",
            "@id": `${siteUrl}/#ngo`,
            name: "Sevaa Foundation",
          },
        },
      })),
    }),
    [campaigns]
  );

  return (
    <section
      id="campaigns"
      aria-labelledby="campaigns-heading"
      className="py-16 bg-[#fcfcfc] font-inter"
    >
      {campaigns.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify(campaignSectionSchema)}
        </script>
      )}

      <div className="max-w-8xl mx-auto px-6 lg:px-8">
        {/* Official Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 border-b border-slate-100 pb-8">
          <div className="max-w-xl">
            <h2
              id="campaigns-heading"
              className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight uppercase"
            >
              Current <span className="text-[#0f766e]">Initiatives</span>
            </h2>

            <p className="text-slate-500 mt-2 font-medium text-sm">
              Transparency-driven social services by Sevaa Foundation.
            </p>
          </div>

          <div className="mt-4 md:mt-0">
            <div className="bg-white px-4 py-2 rounded-lg border border-slate-200 shadow-sm flex items-center gap-2">
              <span
                className="w-2 h-2 bg-green-500 rounded-full animate-pulse"
                aria-hidden="true"
              ></span>

              <span className="text-[10px] font-black text-slate-700 uppercase tracking-widest">
                Active & Verified
              </span>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div
            className="flex justify-center py-20"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <Loader2
              className="animate-spin text-[#0f766e]"
              size={40}
              aria-hidden="true"
            />
            <span className="sr-only">Loading Sevaa Foundation campaigns...</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {campaigns.map((c, i) => {
              const raised = Number(c.raised_amount || 0);
              const goal = Number(c.goal_amount || 1);
              const percentage = Math.min(Math.round((raised / goal) * 100), 100);

              const refId = c.slug
                ? c.slug.substring(0, 3).toUpperCase() + `-${c.id}`
                : `REF-${c.id}`;

              return (
                <motion.article
                  key={c.id}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col"
                  aria-labelledby={`campaign-title-${c.id}`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={getFirstImage(c.image_url)}
                      className="w-full h-full object-cover"
                      alt={`${c.title} - Sevaa Foundation NGO campaign for donation and social welfare in India`}
                      loading="lazy"
                    />

                    <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-md text-white text-[9px] font-black px-2.5 py-1 rounded uppercase tracking-widest">
                      Ref ID: {refId}
                    </div>
                  </div>

                  <div className="p-6 flex flex-col flex-grow">
                    <h3
                      id={`campaign-title-${c.id}`}
                      className="text-lg font-extrabold text-slate-900 mb-2 tracking-tight leading-tight line-clamp-1"
                      title={c.title}
                    >
                      {c.title}
                    </h3>

                    <p
                      className="text-slate-500 text-xs font-medium mb-6 leading-relaxed line-clamp-2"
                      title={c.short_description}
                    >
                      {c.short_description}
                    </p>

                    <div className="mt-auto">
                      <div className="flex justify-between items-end mb-3">
                        <div className="flex flex-col">
                          <span className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">
                            Raised
                          </span>

                          <span className="text-base font-black text-[#0f766e]">
                            ₹{raised.toLocaleString("en-IN")}
                          </span>
                        </div>

                        <div className="text-right">
                          <span className="text-[11px] font-black text-orange-600 bg-orange-50 px-2 py-0.5 rounded-md border border-orange-100">
                            {percentage}%
                          </span>
                        </div>
                      </div>

                      <div
                        className="w-full bg-slate-100 h-2 rounded-full overflow-hidden mb-6"
                        role="progressbar"
                        aria-label={`${c.title} donation progress`}
                        aria-valuenow={percentage}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      >
                        <motion.div
                          initial={{ width: 0 }}
                          whileInView={{ width: `${percentage}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 1.2, ease: "easeOut" }}
                          className="bg-gradient-to-r from-orange-500 to-orange-600 h-full rounded-full"
                        ></motion.div>
                      </div>

                      <button
                        onClick={() => openDonate(c.id)}
                        className="w-full bg-[#0f766e] hover:bg-[#0d5e58] text-white text-[11px] font-black py-4 rounded-xl transition-all uppercase tracking-[0.2em] shadow-lg shadow-teal-900/5 active:scale-95"
                        aria-label={`Contribute to the ${c.title} NGO campaign`}
                      >
                        Contribute
                      </button>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>
        )}

        {/* 80G Tax Exemption Note */}
        <div className="mt-12 text-center">
          <p className="inline-flex items-center gap-2 bg-slate-50 border border-slate-100 px-5 py-2 rounded-full text-[10px] font-bold text-slate-400 uppercase tracking-widest">
            <span className="text-orange-500" aria-hidden="true">
              ★
            </span>{" "}
            All donations qualify for 80G Tax Exemption benefits
          </p>
        </div>
      </div>
    </section>
  );
};

export default Campaigns;